import React, { useState, useCallback } from 'react';
import Drawer from 'devextreme-react/drawer';
import { Template } from 'devextreme-react/core/template';

import { useNavigate } from 'react-router';

import { AppHeader, SideNavigationMenu, Footer } from '../../components';
import { useScreenSize } from '../../utils/media-query';
import { useMenuPatch } from '../../utils/patches';
import { useAppSelector, useAppDispatch } from '../../contexts/hooks';
import type { SideNavToolbarProps } from '../../types';

import './side-nav-inner-toolbar.scss';
import { pushNotification, setCurrentPath } from '../../contexts/slices/notifications';
import { NewUID } from '../../utils/uidLIS';
import { formatMessage } from 'devextreme/localization';

export const SideNavOuterToolbar = ({ title, children }: React.PropsWithChildren<SideNavToolbarProps>) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isXSmall, isLarge } = useScreenSize();
  const [patchCssClass, onMenuReady] = useMenuPatch();
  const [menuStatus, setMenuStatus] = useState(isLarge ? MenuStatus.Opened : MenuStatus.Closed);
  const { Mode } = useAppSelector((x) => x.globalOrders.CurrentOrder);
  const { CurrentPath } = useAppSelector((x) => x.notifications);

  const toggleMenu = useCallback(({ event }) => {
    setMenuStatus((prevMenuStatus) => (prevMenuStatus === MenuStatus.Closed ? MenuStatus.Opened : MenuStatus.Closed));
    event.stopPropagation();
  }, []);

  const temporaryOpenMenu = useCallback(() => {
    setMenuStatus((prevMenuStatus) => (prevMenuStatus === MenuStatus.Closed ? MenuStatus.TemporaryOpened : prevMenuStatus));
  }, []);

  const onOutsideClick = useCallback(() => {
    setMenuStatus((prevMenuStatus) => (prevMenuStatus !== MenuStatus.Closed && !isLarge ? MenuStatus.Closed : prevMenuStatus));
    return !isLarge;
  }, [isLarge]);

  const onNavigationChanged = useCallback(
    ({ itemData: { path }, event, node }) => {
      // Cuando no hay path o el nodo ya está seleccionado, prevenir acción.
      if (!path || node.selected) {
        event.preventDefault();
        return;
      }
      // Nota: antes se bloqueaba la navegación cuando el menú estaba cerrado (compact mode).
      // Eso provocaba que al hacer click en icon-only el tablero se marcara pero no se ejecutaran
      // los filtros ni la navegación. Eliminamos ese bloqueo para permitir navegación desde
      // el modo compacto. Si se desea comportamiento distinto (abrir primero y luego navegar),
      // lo podemos ajustar.
      //Notificamos sobre cambios en ordenes
      if (CurrentPath === '/admin-orders') {
        if (Mode !== 'view') {
          dispatch(pushNotification({ Id: NewUID(), Type: 'order', Url: CurrentPath, State: 'pending', Message: formatMessage('OrdLeavePending') }));
        }
      }
      dispatch(setCurrentPath(path));
      navigate(path);
      if (!isLarge || menuStatus === MenuStatus.TemporaryOpened) {
        setMenuStatus(MenuStatus.Closed);
        event.stopPropagation();
      }
    },
    [navigate, menuStatus, isLarge, Mode]
  );

  return (
    <div className='side-nav-outer-toolbar'>
      {/* @ts-ignore */}
      <AppHeader className='layout-header' menuToggleEnabled toggleMenu={toggleMenu} title={title} />
      <Drawer className={['drawer layout-body', patchCssClass].join(' ')} position='before' closeOnOutsideClick={onOutsideClick} openedStateMode={isLarge ? 'shrink' : 'overlap'} revealMode={isXSmall ? 'slide' : 'expand'} minSize={isXSmall ? 0 : 48} maxSize={250} shading={isLarge ? false : true} opened={menuStatus === MenuStatus.Closed ? false : true} template='menu'>
        <div className='content'>
          {React.Children.map(children, (item) => {
            return React.isValidElement(item) && item.type !== Footer && item;
          })}
        </div>
        <Template name='menu'>
          {/* @ts-ignore */}
          <SideNavigationMenu compactMode={menuStatus === MenuStatus.Closed} selectedItemChanged={onNavigationChanged} openMenu={temporaryOpenMenu} onMenuReady={onMenuReady} />
        </Template>
      </Drawer>
    </div>
  );
};

const MenuStatus = {
  Closed: 1,
  Opened: 2,
  TemporaryOpened: 3,
};
